import * as React from 'react';

import type { BaseComponentProps } from '@coursera/cds-common';
import { Button } from '@coursera/cds-core';

import type { PillsForScreenSize } from 'bundles/browse-common/components/Pill/PillGroup';
import { useItemsCollection } from 'bundles/browse-common/components/Pill/items-collection';
import useKeyboardFocuser from 'bundles/browse-common/components/Pill/useKeyboardFocuser';
import useKeyboardNavigation from 'bundles/browse-common/components/Pill/useKeyboardNavigation';
import { usePillScreenSize } from 'bundles/browse-common/components/Pill/usePillScreenSize';

import _t from 'i18n!nls/browse-common';

export type Props = BaseComponentProps<'div'> & {
  role: 'radiogroup' | 'group';
  shouldCollapse: boolean;
  pillsForScreenSize: PillsForScreenSize;
  onExpand?: () => void;
  onCollapse?: () => void;
  children?: React.ReactNode;
};

const PillCollection = (props: Props) => {
  const { children, role, shouldCollapse, pillsForScreenSize, onExpand, onCollapse, ...rest } = props;
  const collection = useItemsCollection<HTMLButtonElement, { role: 'button' | 'checkbox' | 'radio' }>();
  const [isExpanded, setIsExpanded] = React.useState(!shouldCollapse);
  const { pillsToShow } = usePillScreenSize(pillsForScreenSize);

  const { onKeyDown } = useKeyboardNavigation({
    elements: collection?.items.map((item) => item.element),
    disableArrowsNavigation: collection?.commonProps.role !== 'radio',
  });

  const { containerRef, handleKeyboardEvent } = useKeyboardFocuser({
    isExpanded,
    pillsToShow,
  });

  const childrenArray = React.Children.toArray(children);
  const shouldShowExpandButton = shouldCollapse && childrenArray.length > pillsToShow;
  const visiblePills = isExpanded ? childrenArray : childrenArray.slice(0, pillsToShow);

  const handleToggleExpand = () => {
    const newIsExpanded = !isExpanded;
    setIsExpanded(newIsExpanded);

    if (newIsExpanded) {
      onExpand?.();
    } else {
      onCollapse?.();
    }
  };

  return (
    <div
      ref={containerRef}
      role={role}
      onKeyDown={(event) => {
        onKeyDown(event)?.click();
      }}
      {...rest}
    >
      {visiblePills}
      {shouldShowExpandButton && (
        <Button variant="ghost" size="small" onClick={handleToggleExpand} onKeyDown={handleKeyboardEvent}>
          {isExpanded ? _t('Show fewer') : _t('Show more')}
        </Button>
      )}
    </div>
  );
};

export default PillCollection;
